/**
 * ChatContextualPrompts - Displays contextual suggestions above the chat input
 * Shows page context, key concepts, and suggested prompts to help users get started
 */

import React from 'react';
import { BookOpenIcon, SparklesIcon } from '@heroicons/react/24/outline';

export interface ChatContextualPromptsProps {
  pageNumber: number;
  chapterName: string | null;
  keyConcept: string | null;
  onPromptClick: (prompt: string) => void;
}

export const ChatContextualPrompts: React.FC<ChatContextualPromptsProps> = ({
  pageNumber,
  chapterName,
  keyConcept,
  onPromptClick
}) => {
  // Generate dynamic prompt based on key concept
  const dynamicPrompt = keyConcept
    ? `Explain "${keyConcept}" in simple terms`
    : null;

  // Fixed prompts that are always useful
  const fixedPrompts = [
    'Summarize this page for me',
    'What should I remember from this?'
  ];

  // Combine prompts: dynamic first (if available), then fixed
  const prompts = dynamicPrompt
    ? [dynamicPrompt, ...fixedPrompts]
    : fixedPrompts;

  return (
    <div className="chat-contextual-prompts mb-3 p-3 bg-slate-50/80 dark:bg-slate-800/30 rounded-xl border border-slate-200/50 dark:border-slate-700/30 backdrop-blur-sm">
      {/* Page indicator */}
      <div className="flex items-center gap-2 mb-2">
        <BookOpenIcon className="h-4 w-4 text-cyan-500 dark:text-cyan-400" />
        <span className="text-sm text-slate-600 dark:text-slate-400">
          Page {pageNumber}
          {chapterName && (
            <span className="text-slate-400 dark:text-slate-500">
              {' '}: {chapterName}
            </span>
          )}
        </span>
      </div>

      {/* Key concept */}
      {keyConcept && (
        <div className="flex items-start gap-2 mb-3">
          <SparklesIcon className="h-4 w-4 text-amber-500 dark:text-amber-400 mt-0.5 flex-shrink-0" />
          <span className="text-sm text-slate-700 dark:text-slate-300">
            Key concept:{' '}
            <span className="font-medium text-cyan-600 dark:text-cyan-400">
              {keyConcept}
            </span>
          </span>
        </div>
      )}

      {/* Try asking prompts */}
      <div className="space-y-2">
        <span className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
          Try asking
        </span>
        <div className="flex flex-wrap gap-2">
          {prompts.map((prompt, idx) => (
            <button
              key={idx}
              onClick={() => onPromptClick(prompt)}
              className="px-3 py-1.5 text-xs bg-white dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600/50 rounded-full hover:bg-cyan-50 dark:hover:bg-cyan-900/20 hover:border-cyan-300 dark:hover:border-cyan-600/50 hover:text-cyan-700 dark:hover:text-cyan-300 transition-all text-slate-600 dark:text-slate-300 shadow-sm hover:shadow"
            >
              {prompt}
            </button>
          ))}
        </div>
      </div>

      {/* Highlight hint */}
      <div className="mt-3 pt-2 border-t border-slate-200/50 dark:border-slate-700/30">
        <p className="text-xs text-slate-400 dark:text-slate-500 flex items-center gap-1.5">
          <span className="text-base">&#9998;</span>
          <span>Highlight any text in the PDF to ask about it directly</span>
        </p>
      </div>
    </div>
  );
};

export default ChatContextualPrompts;
